import './types'
import { getFiscalDateFromPeriod } from '../extra/fiscalYearUtils'
import { periodKind } from '../../../../_constants/Code'

/*==================================
  人数カウント設定画面で利用する共通関数（CSV・データ解析処理）
===================================*/
/**
 * 雇用区分CSVデータの二次元配列を加工し、オブジェクト配列に変換する
 *
 * @param {string[][]} csvData - ヘッダー行を含むCSVの二次元配列
 * @returns {Object[]} 加工後の雇用区分データ配列
 */
export function parseEmploymentTypeCsv(csvData) {
  if (!csvData.length) return []

  const rows = csvData.slice(1) // ヘッダー除去
  return rows.map((row) => {
    const [corporationNumberRaw, companyNameRaw, employmentTypeRaw, startDateRaw, endDateRaw, displayOrderRaw] = row

    return {
      employmentType: employmentTypeRaw,
      validStartDate: startDateRaw === '' ? null : startDateRaw.replace(/\//g, '-'),
      validEndDate: endDateRaw === '' ? null : endDateRaw.replace(/\//g, '-'),
      displayOrder: displayOrderRaw
    }
  })
}

/**
 * 雇用区分設定CSVデータの二次元配列を加工し、オブジェクト配列に変換する
 *
 * @param {string[][]} csvData - ヘッダー行を含むCSVの二次元配列
 * @returns {Object[]} 加工後の雇用区分データ配列
 */
export function parseEmploymentTypeSettingCsv(csvData) {
  if (!csvData.length) return []

  const rows = csvData.slice(1) // ヘッダー除去
  return rows.map((row) => {
    const [
      corporationNumberRaw,
      companyNameRaw,
      fiscalYearRaw,
      fiscalYearMonthRaw,
      employmentTypeRaw,
      securitiesEmployeeTypeRaw,
      fteEstimatedFactorRaw,
      fteActualWorkingHoursCalcMethodRaw
    ] = row

    return {
      fiscalYear: fiscalYearRaw,
      fiscalYearMonth: fiscalYearMonthRaw === '' ? null : fiscalYearMonthRaw.replace(/\//g, '-'),
      employmentType: employmentTypeRaw,
      securitiesEmployeeType: securitiesEmployeeTypeRaw,
      fteEstimatedFactor: fteEstimatedFactorRaw,
      fteActualWorkingHoursCalcMethod: fteActualWorkingHoursCalcMethodRaw
    }
  })
}

/**
 * 所定労働時間設定（月）CSVデータの二次元配列を加工し、オブジェクト配列に変換する
 *
 * @param {string[][]} csvData - ヘッダー行を含むCSVの二次元配列
 * @returns {Object[]} 加工後の雇用区分データ配列
 */
export function parsePrescribedWorkingHoursMonthCsv(csvData) {
  if (!csvData.length) return []

  const rows = csvData.slice(1) // ヘッダー除去
  return rows.map((row) => {
    const [
      corporationNumberRaw,
      companyNameRaw,
      fiscalYearRaw,
      fiscalYearMonthRaw,
      prescribedWorkingHoursPerDayRaw,
      monthRaw,
      workingDaysRaw,
      prescribedWorkingHoursPerMonthRaw
    ] = row

    return {
      fiscalYear: fiscalYearRaw,
      fiscalYearMonth: fiscalYearMonthRaw === '' ? null : fiscalYearMonthRaw.replace(/\//g, '-'),
      prescribedWorkingHoursPerDay: prescribedWorkingHoursPerDayRaw,
      month: monthRaw,
      workingDays: workingDaysRaw,
      prescribedWorkingHoursPerMonth: prescribedWorkingHoursPerMonthRaw
    }
  })
}

/**
 * 所定労働時間設定（年）CSVデータの二次元配列を加工し、オブジェクト配列に変換する
 *
 * @param {string[][]} csvData - ヘッダー行を含むCSVの二次元配列
 * @returns {Object[]} 加工後の雇用区分データ配列
 */
export function parsePrescribedWorkingHoursYearCsv(csvData) {
  if (!csvData.length) return []

  const rows = csvData.slice(1) // ヘッダー除去
  return rows.map((row) => {
    const [
      corporationNumberRaw,
      companyNameRaw,
      fiscalYearRaw,
      fiscalYearMonthRaw,
      weeksPerYearRaw,
      prescribedWorkingHoursPerWeekRaw,
      prescribedWorkingHoursPerYearRaw
    ] = row

    return {
      fiscalYear: fiscalYearRaw,
      fiscalYearMonth: fiscalYearMonthRaw === '' ? null : fiscalYearMonthRaw.replace(/\//g, '-'),
      weeksPerYear: weeksPerYearRaw,
      prescribedWorkingHoursPerWeek: prescribedWorkingHoursPerWeekRaw,
      prescribedWorkingHoursPerYear: prescribedWorkingHoursPerYearRaw
    }
  })
}

/**
 * 人数カウント設定CSVデータの二次元配列を加工し、オブジェクト配列に変換する
 *
 * @param {string[][]} csvData - ヘッダー行を含むCSVの二次元配列
 * @returns {Object[]} 加工後の雇用区分データ配列
 */
export function parseEmployeeCountSetting(csvData) {
  if (!csvData.length) return []

  const rows = csvData.slice(1) // ヘッダー除去
  return rows.map((row) => {
    const [
      corporationNumberRaw,
      companyNameRaw,
      fiscalYearRaw,
      fiscalYearMonthRaw,
      roundingMethodRaw,
      employeeOnLeaveTargetRaw,
      intraGroupSecondmentSourceRaw,
      intraGroupSecondmentDestinationRaw,
      interGroupSecondmentSourceRaw,
      interGroupSecondmentDestinationRaw,
      concurrentSecondmentTargetRaw
    ] = row

    return {
      fiscalYear: fiscalYearRaw,
      fiscalYearMonth: fiscalYearMonthRaw === '' ? null : fiscalYearMonthRaw.replace(/\//g, '-'),
      roundingMethod: roundingMethodRaw,
      employeeOnLeaveTarget: employeeOnLeaveTargetRaw,
      intraGroupSecondmentSource: intraGroupSecondmentSourceRaw,
      intraGroupSecondmentDestination: intraGroupSecondmentDestinationRaw,
      interGroupSecondmentSource: interGroupSecondmentSourceRaw,
      interGroupSecondmentDestination: interGroupSecondmentDestinationRaw,
      concurrentSecondmentTarget: concurrentSecondmentTargetRaw
    }
  })
}

/**
 * データ作成タスク管理から年次タスク状況を取得
 *
 * @param {Object[]} dataCreateTask - データ作成タスク管理情報
 * @returns {Object[]} 年次タスク状況
 */
export function filterDataCreateTask(dataCreateTask, accPeriod) {
  if (!dataCreateTask.length) return []

  return dataCreateTask
    .filter((task) => task.PeriodKind === periodKind.year.value)
    .map((task) => {
      const { fiscalYear, fiscalYearMonth } = getFiscalDateFromPeriod(task.PeriodValue, accPeriod)
      return {
        fiscalYear,
        fiscalYearMonth,
        PeriodKind: task.PeriodKind,
        PeriodValue: task.PeriodValue,
        TaskStatus: task.TaskStatus,
        DataFixStatus: task.DataFixStatus
      }
    })
}

/**
 * 出向情報のファイルリストから出向情報アップロード年度リストを取得
 *
 * @param {string[]} } FileList - 出向情報のファイルリスト（例: ["/std_secondment_2024.csv", ...]）
 * @returns {string[]} } 出向情報のファイルリスト（例: ["2024年度", ...]）
 */
export function parseStandardFormatSecondment(fileList, accPeriod) {
  return fileList.map((fileName) => {
    // 正規表現で4桁の数字（年度）を抽出
    const match = fileName.match(/(\d{4})/)
    if (match) {
      return match[1] + '年度'
    }
    return []
  })
}
